import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Brand primary CTA — hotter brushed-metal sheen so it reads as the
        // hero action on the page (Save, Create, Add Lead, etc.).
        default:
          "metallic-hot bg-primary text-primary-foreground shadow hover:bg-primary/90",
        // Destructive solid fill — subtle metallic finish.
        destructive:
          "metallic bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        // Outline buttons have no fill → no metallic.
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        // Secondary filled buttons — subtle metallic finish.
        secondary:
          "metallic bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        // Ghost / link — text-only, no fill → no metallic.
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
